import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { find as _find, get as _get } from 'lodash';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { debounceTime, take, takeUntil } from 'rxjs/operators';

import { BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
import { env } from '@amfam/environment';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { BillingPaymentPaths, Party } from '@amfam/shared/models';
import { QuickLink } from '@amfam/shared/ui/quick-links';
import { userQuery } from '@amfam/shared/user';
// Store
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService, UtilService, WindowRef } from '@amfam/shared/utility/shared-services';
import {
  Action,
  DockingBarService,
  ErrorLevels,
  ErrorObj,
  PageTitleService,
  ValidationService
} from '@amfam/ui-kit';

import * as fromRoot from '../../core/store';
import { BillAccountContactService } from './bill-account-contact.service';
import { ContactBillingInternalEmail } from './contact-billing-internalEmail.model';

@Component({
  selector: 'app-bill-account-contact',
  templateUrl: './bill-account-contact.component.html',
  styleUrls: ['./bill-account-contact.component.scss'],
  standalone: false
})
export class BillAccountContactComponent implements OnInit, OnDestroy {
  // Form elements
  contactBillingForm: UntypedFormGroup;
  firstNameInput: AbstractControl;
  lastNameInput: AbstractControl;
  addressInput: AbstractControl;
  cityInput: AbstractControl;
  stateInput: AbstractControl;
  zipInput: AbstractControl;
  currentContactReason: string;
  isContactBillingToggleEnabled: boolean;
  public get phoneInput(): AbstractControl {
    return this.contactBillingForm.controls['phoneInput'];
  }
  public get phoneInputList(): AbstractControl {
    return this.contactBillingForm.controls['phoneInputList'];
  }
  emailInput: AbstractControl;
  billingAcctNumberInput: AbstractControl;
  contactReasonInput: AbstractControl;
  contactSubReasonInput: AbstractControl;
  messageInput: AbstractControl;

  // Options arrays.
  states: Array<string> = [];
  quickLinks: Array<QuickLink>;
  billAccounts: any[] = [];
  contactMode: Array<string> = [];
  phoneList: Array<string> = [];
  contactReasons: Array<string> = [];

  currentContactMode: string;
  contactBillingReason: string;
  amfamDotCom: string;
  partnerCustomerCareNumber: string;
  amfamCustomerCareNumber: string;
  contactBillingError = false;
  errorList: Array<ErrorObj> = [];

  private party: Party;
  public isContactBillingError: Observable<boolean> = of(false);

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private copyService: CopyService,
    private dockingService: DockingBarService,
    private pageTitle: PageTitleService,
    private utilService: UtilService,
    private route: ActivatedRoute,
    private win: WindowRef,
    private store: Store<fromRoot.RootState>,
    private billAccountContactService: BillAccountContactService,
    public roleService: ImpersonateRolesService,
    private analyticsFacade: AnalyticsFacade,
    private featureFlagService: FeatureFlagService
  ) {}

  ngOnInit() {
    this.isContactBillingToggleEnabled = this.featureFlagService.isEnabled('contact_billing');

    this.amfamDotCom = env.links.amfamDotComUrl;
    this.amfamCustomerCareNumber = this.copyService.getCopy(
      'shared.amfamBillingContactPhoneNumber'
    );
    this.setupForm();
    this.store
      .select(BrandSelectors.selectPrettyCusCareNumber)
      .pipe(take(1))
      .subscribe(number => {
        this.partnerCustomerCareNumber = number;
      });
    // Set the bill account ids from the store
    // Prefill user data
    // AS: userQuery.getPartyDetails is ideally supposed to return Billing address as well, but it does not for now.
    // TODO: Check this once all the API are developed.
    combineLatest(
      this.store.select(BillAccountsSelectors.selectBillAccounts),
      this.store.select(userQuery.selectMyAccountEmail),
      this.store.select(userQuery.selectPartyDetails),
      this.store.select(BrandSelectors.selectIsPartner),
      (billAccounts, myAccountEmail, party, isConnectPartner) => ({
        billAccounts: billAccounts,
        myAccountEmail: myAccountEmail,
        party: party,
        isConnectPartner: isConnectPartner
      })
    )
      .pipe(takeUntil(this.stop$), debounceTime(300))
      .subscribe(state => {
        this.quickLinks = [
          new QuickLink('Make a Payment', '/billing'),
          new QuickLink('Request a Change', '/policies/changerequest'),
          new QuickLink('Contact a Billing Specialist', '/billing/contact'),
          new QuickLink('FAQs', `${this.amfamDotCom}resources/faqs/payments`, 'amfam')
        ]
          .filter(
            link =>
              // filter Request a Change and FAQ links if non-AFI partner
              !(
                (_get(link, 'text') === 'Request a Change' || _get(link, 'text') === 'FAQs') &&
                state.isConnectPartner
              )
          )
          .filter(
            link =>
              // fitler Contact a Billing Specialist link if contact billing is toggled off
              !(
                _get(link, 'text') === 'Contact a Billing Specialist' &&
                !this.isContactBillingToggleEnabled
              )
          );

        if (_get(state, 'billAccounts')) {
          this.billAccounts = state.billAccounts;
        }
        if (_get(state, 'party.firstName')) {
          this.firstNameInput.setValue(state.party.firstName);
        }
        if (_get(state, 'party.lastName')) {
          this.lastNameInput.setValue(state.party.lastName);
        }
        if (_get(state, 'myAccountEmail.emailAddress')) {
          this.emailInput.setValue(state.myAccountEmail.emailAddress);
        }
        if (_get(state, 'party')) {
          this.party = state.party;
          if (this.party.emails && this.party.emails.length) {
            const emailAddress = this.party.emails;
            const emailAddressInput = this.contactBillingForm.get('emailInput');
            if (!emailAddressInput.value) {
              emailAddressInput.setValue(emailAddress);
            }
          }
          if (this.party.phones && this.party.phones.length) {
            this.phoneList = [];
            this.party.phones.forEach(phoneNum => {
              this.phoneList.push(phoneNum.tenDigitPhoneNumber);
            });
            // AS : If we do not have the list remove the control from the form.
            if (this.phoneList.length) {
              this.contactBillingForm.addControl(
                'phoneInputList',
                new UntypedFormControl(['', Validators.compose([Validators.required])])
              );
              this.contactBillingForm.removeControl('phoneInput');
            }
          }
        }
      });
  }

  private setupForm() {
    this.contactBillingForm = this.formBuilder.group({
      firstNameInput: ['', Validators.compose([Validators.required])],
      lastNameInput: ['', Validators.compose([Validators.required])],
      phoneInput: [
        '',
        Validators.compose([Validators.required, ValidationService.phoneNumberValidator])
      ],
      emailInput: [
        '',
        Validators.compose([
          Validators.required,
          ValidationService.allowedEmailCharactersValidator,
          ValidationService.emailValidator
        ])
      ],

      billingAcctNumberInput: ['', Validators.compose([Validators.required])],

      messageInput: ['', Validators.compose([Validators.maxLength(250)])],
      contactMode: ['', Validators.compose([Validators.required])],
      contactReasons: ['', Validators.compose([Validators.required])]
    });

    this.firstNameInput = this.contactBillingForm.controls['firstNameInput'];
    this.lastNameInput = this.contactBillingForm.controls['lastNameInput'];

    this.addressInput = this.contactBillingForm.controls['addressInput'];
    this.cityInput = this.contactBillingForm.controls['cityInput'];
    this.stateInput = this.contactBillingForm.controls['stateInput'];
    this.zipInput = this.contactBillingForm.controls['zipInput'];
    this.emailInput = this.contactBillingForm.controls['emailInput'];
    this.billingAcctNumberInput = this.contactBillingForm.controls['billingAcctNumberInput'];

    this.contactSubReasonInput = this.contactBillingForm.controls['contactSubReasonInput'];
    this.messageInput = this.contactBillingForm.controls['messageInput'];

    this.states = this.copyService.getCopy('shared.usStates');

    this.contactMode = this.copyService.getCopy('billing.contactMode');

    this.contactReasons = this.copyService.getCopy('billing.contactReasons');

    this.dockingService.registerHeading('Billing & Payments');
    this.dockingService.registerAction(
      new Action('Billing & Payments', () => {
        this.store.dispatch(
          fromRouterActions.Go({
            path: [BillingPaymentPaths.BILLING_SECTION_PAGE]
          })
        );
      })
    );
    this.pageTitle.set('Billing & Payments - Contact');

    // Preselect bill account number
    this.route.params.pipe(takeUntil(this.stop$)).subscribe(params => {
      if (
        params['id'] &&
        params['id'] !== 'billingStatements' &&
        params['id'] !== 'paymentHistory'
      ) {
        this.billingAcctNumberInput.setValue(params['id']);
      } else if (params['id'] === 'billingStatements') {
        this.contactReasonInput.setValue(
          this.copyService.getCopy('billing.contactReasonsBillingStatements')
        );
      } else if (params['id'] === 'paymentHistory') {
        this.contactReasonInput.setValue(
          this.copyService.getCopy('billing.contactReasonsPaymentHistory')
        );
      }
    });
  }
  onContactReasonChange(value: string) {
    this.currentContactReason = value;
  }
  onContactModeChange(value: string) {
    this.currentContactMode = value;
    if (value === 'Phone') {
      this.emailInput.setValidators(null);
      this.emailInput.updateValueAndValidity();

      if (this.phoneInput) {
        this.phoneInput.setValidators(
          Validators.compose([Validators.required, ValidationService.phoneNumberValidator])
        );
        this.phoneInput.updateValueAndValidity();
      } else if (this.phoneInputList) {
        this.phoneInputList.setValidators(Validators.compose([Validators.required]));
        this.phoneInputList.updateValueAndValidity();
      }
    } else {
      if (this.phoneInput) {
        this.phoneInput.setValidators(null);
        this.phoneInput.updateValueAndValidity();
      } else {
        this.phoneInputList.setValidators(null);
        this.phoneInputList.updateValueAndValidity();
      }

      this.emailInput.setValidators(
        Validators.compose([
          Validators.required,
          ValidationService.allowedEmailCharactersValidator,
          ValidationService.emailValidator
        ])
      );
      this.emailInput.updateValueAndValidity();
    }
  }

  private getBillAccountType(billAccountNumber: string) {
    let billAccountObj: any;
    let accountType: string;
    if (billAccountNumber) {
      if (this.billAccounts && this.billAccounts.length > 0) {
        billAccountObj = _find(this.billAccounts, function (billAccount) {
          return billAccount.billAccountNumber === billAccountNumber;
        });
        accountType = billAccountObj.accountType;
      }
    }
    return accountType;
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
    this.billAccountContactService.disposeSub();
  }

  submit() {
    const phone =
      (this.phoneInput && this.phoneInput.value) ||
      (this.phoneInputList && this.phoneInputList.value) ||
      '';
    const contactInternalEmailObj = new ContactBillingInternalEmail(
      this.firstNameInput.value,
      this.lastNameInput.value,
      this.currentContactReason,
      this.currentContactMode,
      this.emailInput.value || '',
      phone,
      this.billingAcctNumberInput.value,
      this.messageInput.value || '',
      this.getBillAccountType(this.billingAcctNumberInput.value)
    );

    this.billAccountContactService
      .generateInternalEmails(contactInternalEmailObj)
      .then(resInternal => {
        // AS: Display Success message.
        this.store.dispatch(
          fromRouterActions.Go({
            path: [BillingPaymentPaths.BILLING_CONTACT_CONFIRMATION]
          })
        );
      })
      .catch(err => {
        this.isContactBillingError = of(true);
        const message = this.copyService.getCopy('billing.contactBillinErrorMessage');
        this.errorList = this.showErrorMessage([message]);
        this.utilService.scrollToTop();
      });
  }

  private showErrorMessage(
    errorMessageList: Array<string>,
    errorLevel?: ErrorLevels
  ): Array<ErrorObj> {
    let errorList: Array<ErrorObj> = [];
    errorMessageList.forEach((msg: string) => {
      if (!errorList.find(errorObj => errorObj.errorMessage === msg)) {
        errorList = [
          ...errorList,
          { errorMessage: msg, errorLevel: errorLevel || ErrorLevels.HIGH }
        ];
      }
    });
    return errorList;
  }
}
